import Image from "next/image";
import { Fragment, type CSSProperties, type ReactNode } from "react";
import { displayFont } from "./band-helpers";
import type {
  BlockAppearance,
  CtaButton,
  ShowcaseBlock,
  ShowcaseImage,
} from "@/lib/storefront-types";

/* ------------------------------------------------------------------ */
/*  Per-appearance theme — from the "Aso Rock App Launch Banner" design */
/*  Default (dark/light) = green Fresh brand; "frozen" = blue brand.    */
/* ------------------------------------------------------------------ */

type Theme = {
  bannerBg: string;
  bannerShadow: string;
  glow: string;
  amberGlow: string;
  accent: string;
  glyph: string;
  badgeBig: string;
  badgeSmall: string;
  outerBg: string;
};

const GREEN_THEME: Theme = {
  bannerBg: "linear-gradient(135deg, #0d2a12 0%, #071c0b 52%, #0d2a12 100%)",
  bannerShadow: "0 40px 90px -44px rgba(0,39,0,0.85)",
  glow: "radial-gradient(60% 55% at 72% 42%, rgba(0,150,57,0.42), transparent 70%)",
  amberGlow: "radial-gradient(40% 40% at 78% 78%, rgba(240,181,61,0.16), transparent 70%)",
  accent: "#9cd4a0",
  glyph: "#0d2a12",
  badgeBig: "#102313",
  badgeSmall: "#557256",
  outerBg: "#eef6ef",
};

const FROZEN_THEME: Theme = {
  bannerBg: "linear-gradient(135deg, #0b1d4a 0%, #050f2b 52%, #0b1d4a 100%)",
  bannerShadow: "0 40px 90px -44px rgba(4,14,46,0.85)",
  glow: "radial-gradient(60% 55% at 72% 42%, rgba(40,86,220,0.45), transparent 70%)",
  amberGlow: "radial-gradient(40% 40% at 78% 78%, rgba(120,170,255,0.14), transparent 70%)",
  accent: "#93b4ff",
  glyph: "#0b1d4a",
  badgeBig: "#0e2150",
  badgeSmall: "#5566a0",
  outerBg: "#eef1fb",
};

function getTheme(appearance: BlockAppearance | undefined): Theme {
  return appearance === "frozen" ? FROZEN_THEME : GREEN_THEME;
}

const PHONE_FRAME = "#0a0a0a";
const SIDE_SHADOW =
  "0 40px 64px -34px rgba(0,0,0,0.85), 0 0 0 1.5px rgba(255,255,255,0.06)";
const CENTER_SHADOW =
  "0 54px 92px -34px rgba(0,0,0,0.92), 0 0 0 2px rgba(255,255,255,0.07)";
const SCREEN_ASPECT = "1284 / 2778";
const STAR_GOLD = "#f0b53d";

/* ------------------------------------------------------------------ */
/*  Store-badge glyphs (lifted from the design)                        */
/* ------------------------------------------------------------------ */

function AppleGlyph({ color }: { color: string }) {
  return (
    <svg width="26" height="32" viewBox="0 0 24 24" fill={color} aria-hidden="true">
      <path d="M17.05 12.04c-.03-2.6 2.12-3.85 2.22-3.91-1.21-1.77-3.1-2.01-3.77-2.04-1.6-.16-3.13.94-3.94.94-.81 0-2.07-.92-3.4-.9-1.75.03-3.36 1.02-4.26 2.58-1.82 3.16-.47 7.83 1.3 10.4.86 1.26 1.89 2.67 3.24 2.62 1.3-.05 1.79-.84 3.36-.84 1.57 0 2.01.84 3.39.81 1.4-.02 2.28-1.28 3.13-2.55.99-1.46 1.4-2.88 1.42-2.95-.03-.01-2.72-1.04-2.75-4.13zM14.5 4.84c.72-.87 1.2-2.08 1.07-3.28-1.03.04-2.28.69-3.02 1.56-.66.77-1.24 2-1.08 3.18 1.15.09 2.32-.59 3.03-1.46z" />
    </svg>
  );
}

function GooglePlayGlyph() {
  return (
    <svg width="24" height="28" viewBox="0 0 512 512" aria-hidden="true">
      <path fill="#00d3ff" d="M47 24c-5 3-8 8-8 16v432c0 8 3 13 8 16l1 1 242-242v-2L47 24z" />
      <path fill="#00f076" d="M48 24l242 241 67-67L72 13C62 7 54 16 48 24z" />
      <path fill="#fee000" d="M290 265l67 67 96-55c19-11 19-29 0-40l-95-55-68 68z" />
      <path fill="#f63448" d="M48 488l242-223 68 67-286 165c-12 7-22 0-24-9z" />
    </svg>
  );
}

type StoreInfo = { glyph: ReactNode; small: string; big: string };

function detectStore(href: string | null | undefined, glyphColor: string): StoreInfo | null {
  const url = href ?? "";
  if (/apps\.apple\.com|itunes\.apple\.com/i.test(url)) {
    return { glyph: <AppleGlyph color={glyphColor} />, small: "Download on the", big: "App Store" };
  }
  if (/play\.google\.com/i.test(url)) {
    return { glyph: <GooglePlayGlyph />, small: "Get it on", big: "Google Play" };
  }
  return null;
}

/* ------------------------------------------------------------------ */
/*  Two-tone headline — highlight `accent` within `headline`           */
/* ------------------------------------------------------------------ */

function renderHeadline(
  headline: string,
  accent: string | null | undefined,
  accentColor: string,
): ReactNode {
  if (!accent) {
    return headline;
  }
  const index = headline.indexOf(accent);
  if (index === -1) {
    return headline;
  }
  return (
    <Fragment>
      {headline.slice(0, index)}
      <span style={{ color: accentColor }}>{accent}</span>
      {headline.slice(index + accent.length)}
    </Fragment>
  );
}

/* ------------------------------------------------------------------ */
/*  Store badge button                                                 */
/* ------------------------------------------------------------------ */

function StoreBadge({ button, theme }: { button: CtaButton; theme: Theme }) {
  const store = detectStore(button.href, theme.glyph);
  return (
    <a
      href={button.href}
      target={button.target === "blank" ? "_blank" : undefined}
      rel={button.target === "blank" ? "noopener noreferrer" : undefined}
      className="inline-flex items-center gap-3 rounded-[14px] bg-white py-3 pl-[18px] pr-[22px] transition-transform duration-200 hover:-translate-y-px"
      style={{ boxShadow: "0 18px 34px -20px rgba(0,0,0,0.6)" }}
    >
      {store?.glyph}
      <span className="flex flex-col text-left leading-[1.1]">
        <span className="text-[11px] font-medium" style={{ color: theme.badgeSmall }}>
          {store ? store.small : button.label}
        </span>
        <span
          className={`${displayFont.className} text-[21px] font-bold`}
          style={{ color: theme.badgeBig }}
        >
          {store ? store.big : ""}
        </span>
      </span>
    </a>
  );
}

/* ------------------------------------------------------------------ */
/*  Phone in the fan trio                                              */
/* ------------------------------------------------------------------ */

type PhoneVariant = "left" | "center" | "right";

function Phone({
  item,
  variant,
}: {
  item: ShowcaseImage;
  variant: PhoneVariant;
}) {
  if (!item.image?.url) {
    return null;
  }

  const isCenter = variant === "center";
  const frameStyle: CSSProperties = isCenter
    ? {
        position: "absolute",
        top: "22px",
        left: "50%",
        transform: "translateX(-50%)",
        width: "190px",
        background: PHONE_FRAME,
        borderRadius: "40px",
        padding: "8px",
        boxShadow: CENTER_SHADOW,
        zIndex: 2,
      }
    : {
        position: "absolute",
        top: "54px",
        [variant === "left" ? "left" : "right"]: "24px",
        transform: `rotate(${variant === "left" ? "-10deg" : "10deg"})`,
        width: "176px",
        background: PHONE_FRAME,
        borderRadius: "32px",
        padding: "7px",
        boxShadow: SIDE_SHADOW,
        zIndex: 1,
      };

  return (
    <div style={frameStyle}>
      <div
        className="relative block w-full overflow-hidden"
        style={{
          borderRadius: isCenter ? "32px" : "26px",
          aspectRatio: SCREEN_ASPECT,
        }}
      >
        <Image
          src={item.image.url}
          alt={item.caption || item.image.alt || "App screenshot"}
          fill
          sizes="190px"
          className="object-cover"
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Showcase block                                                     */
/* ------------------------------------------------------------------ */

export function ShowcaseSection({ block }: { block: ShowcaseBlock }) {
  const theme = getTheme(block.appearance);
  const images = block.images.filter((item) => item.image?.url);
  // Fan layout: centre is the focal screen, flanked by the others.
  let left: ShowcaseImage | undefined;
  let center: ShowcaseImage | undefined;
  let right: ShowcaseImage | undefined;
  if (images.length >= 3) {
    [left, center, right] = images;
  } else if (images.length === 2) {
    [center, right] = images;
  } else if (images.length === 1) {
    [center] = images;
  }

  const buttons = block.buttons.filter((b) => b.enabled);
  const showRating = Boolean(block.ratingScore || block.ratingText);

  return (
    <section
      className="flex w-full items-center justify-center p-[56px_32px] max-[560px]:p-[22px_14px]"
      style={{ background: theme.outerBg }}
    >
      <div
        className="relative w-[min(1200px,100%)] overflow-hidden rounded-[32px]"
        style={{ background: theme.bannerBg, boxShadow: theme.bannerShadow }}
      >
        <div className="pointer-events-none absolute inset-0" style={{ background: theme.glow }} />
        <div className="pointer-events-none absolute inset-0" style={{ background: theme.amberGlow }} />

        <div className="relative flex items-stretch gap-10 p-[44px_52px_44px_56px] max-[1040px]:gap-7 max-[1040px]:p-[40px_40px_40px_44px] max-[880px]:flex-col max-[880px]:items-center max-[880px]:gap-[6px] max-[880px]:p-[44px_32px_40px_32px] max-[880px]:text-center max-[560px]:p-[38px_22px_34px_22px]">
          {/* Copy column */}
          <div className="flex flex-1 basis-0 flex-col justify-center py-3 max-[880px]:items-center max-[880px]:p-0">
            {block.logo?.url ? (
              // eslint-disable-next-line @next/next/no-img-element -- brand logo is an SVG; next/image rejects SVGs without dangerouslyAllowSVG
              <img
                src={block.logo.url}
                alt={block.logo.alt || ""}
                className="mb-[34px] h-12 w-auto self-start max-[880px]:self-center"
              />
            ) : null}

            {block.headline ? (
              // h2, not h1 — the page's H1 belongs to the page intro / hero;
              // this block appears mid-page (e.g. the homepage app banner).
              <h2
                className={`${displayFont.className} m-0 max-w-[15ch] text-[46px] font-bold leading-[1.04] tracking-[-0.015em] text-white max-[880px]:max-w-[34ch] max-[560px]:text-[33px]`}
              >
                {renderHeadline(block.headline, block.headlineAccent, theme.accent)}
              </h2>
            ) : null}

            {block.bodyHtml ? (
              <div
                className="mt-[18px] max-w-[44ch] text-[16px] leading-[1.55] max-[880px]:max-w-[34ch] max-[560px]:text-[15px]"
                style={{ color: "rgba(255,255,255,0.72)" }}
                dangerouslySetInnerHTML={{ __html: block.bodyHtml }}
              />
            ) : null}

            {buttons.length > 0 ? (
              <div className="mt-7 flex gap-[14px] max-[880px]:justify-center max-[560px]:flex-col max-[560px]:items-center">
                {buttons.map((button) => (
                  <StoreBadge key={button.label || button.href} button={button} theme={theme} />
                ))}
              </div>
            ) : null}

            {showRating ? (
              <div className="mt-6 flex items-center gap-[14px] text-[14px] max-[880px]:justify-center" style={{ color: "rgba(255,255,255,0.78)" }}>
                <span
                  aria-hidden="true"
                  className="text-[15px] tracking-[2px]"
                  style={{ color: STAR_GOLD }}
                >
                  ★★★★★
                </span>
                {block.ratingScore ? (
                  <strong className="font-bold text-white">{block.ratingScore}</strong>
                ) : null}
                {block.ratingText ? <span>{block.ratingText}</span> : null}
              </div>
            ) : null}
          </div>

          {/* Phone column */}
          {center ? (
            <div className="flex shrink-0 grow-0 basis-[496px] items-center justify-center max-[1040px]:basis-[416px] max-[880px]:mt-[10px] max-[880px]:w-full max-[880px]:basis-auto">
              <div className="flex items-center justify-center h-[484px] w-[496px] max-[1040px]:h-[406.56px] max-[1040px]:w-[416.64px] max-[880px]:h-[454.96px] max-[880px]:w-[466.24px] max-[560px]:h-[319.44px] max-[560px]:w-[327.36px]">
                <div className="relative h-[484px] w-[496px] origin-center scale-100 max-[1040px]:scale-[0.84] max-[880px]:scale-[0.94] max-[560px]:scale-[0.66]">
                  {left ? <Phone item={left} variant="left" /> : null}
                  {right ? <Phone item={right} variant="right" /> : null}
                  <Phone item={center} variant="center" />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
